import React from 'react';

import {styled} from '@storybook/theming';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';

import {format} from '../utils/githubHelpers';

interface ComponentProps {
    avatar?: string;
    body: string;
    date?: Date;
    type?: 'error' | 'issue';
}

/**
 * Comment.
 *
 * @param props        Component props.
 * @param props.avatar The avatar.
 * @param props.body   The body.
 * @param props.date   The date.
 * @param props.type   The type.
 * @returns The component.
 */
const Comment = ({avatar, body, date, type}: ComponentProps) => (
    <CommentWrapper>
        {type !== 'error' && (
            <>
                <CommentAvatar src={avatar!} />
                <CommentBody>
                    <CommentInfo>{type === 'issue' ? 'Issue created' : 'Commented'} at {format(date!)}</CommentInfo>
                    <CommentText>
                        <ReactMarkdown
                            // eslint-disable-next-line react/no-children-prop
                            children={body}
                            rehypePlugins={[rehypeHighlight]}
                            remarkPlugins={[remarkBreaks, remarkGfm]}
                        />
                    </CommentText>
                </CommentBody>
            </>
        )}
        {type === 'error' && (
            <CommentBody>
                <CommentInfo $hasError>Error</CommentInfo>
                <CommentText $hasError>
                    {body}
                </CommentText>
            </CommentBody>
        )}
    </CommentWrapper>
);

Comment.displayName = 'Comment';
Comment.defaultProps = {testId: null};

export default Comment;

const CommentWrapper = styled.div`
    display: flex;
    gap: 10px;
    padding: 10px;
`;

const CommentAvatar = styled.div<{src: string}>`
    background-image: url(${({src}) => src});
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    border-radius: 50%;
    flex: 0 0 40px;
    height: 40px;
    width: 40px;
`;

const CommentBody = styled.div`
    border: 1px solid rgba(255,255,255,.1);
    border-radius: 5px;
    flex: 1 1 auto;
`;

interface CommentInfoProps {
    $hasError?: boolean;
}

const CommentInfo = styled.div<CommentInfoProps>`
    background-color: #292C2E;
    border-bottom: 1px solid rgba(255,255,255,.1);
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    color: ${({$hasError}) => ($hasError ? '#FF0000' : null)};
    display: flex;
    padding: 5px 16px;
`;

interface CommentTextProps {
    $hasError?: boolean;
}

const CommentText = styled.div<CommentTextProps>`
    color: ${({$hasError}) => ($hasError ? '#FF0000' : null)};
    padding: 10px 16px;
`;